import axios from 'axios'
import { requireSudo } from '@/services/sudo'

// Moroorato admin API. In dev the vue-cli devServer proxies /api → the Laravel backend (see vue.config.js),
// so the default is a same-origin relative path and there is no CORS to configure. Override with
// VUE_APP_API_URL when the panel is served from a different origin than the API.
const baseURL = process.env.VUE_APP_API_URL || '/api/v1/admin'

const STORAGE = {
  token: 'moroorato_admin_token',
  expires: 'moroorato_admin_token_expires',
  admin: 'moroorato_admin_user',
}

const api = axios.create({ baseURL })

export const clearAdminAuthStorage = () => {
  localStorage.removeItem(STORAGE.token)
  localStorage.removeItem(STORAGE.expires)
  localStorage.removeItem(STORAGE.admin)
}

/**
 * Persist a session. Moroorato issues OPAQUE bearer tokens (not JWTs) with an absolute expiry returned by
 * the login endpoint — there is no refresh token, so we simply store the token + its expiry and re-auth
 * when it lapses or the API returns 401.
 */
export const persistAdminSession = ({ accessToken, expiresAt, adminUser } = {}) => {
  if (accessToken) {
    localStorage.setItem(STORAGE.token, accessToken)
  }
  if (expiresAt) {
    localStorage.setItem(STORAGE.expires, expiresAt)
  }
  if (adminUser) {
    localStorage.setItem(STORAGE.admin, JSON.stringify(adminUser))
  }
}

export const getStoredAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE.admin) || 'null')
  } catch (_error) {
    return null
  }
}

const isSessionExpired = () => {
  const expiresAt = localStorage.getItem(STORAGE.expires)
  if (!expiresAt) {
    return false // unknown expiry → let the API be the authority
  }
  return Date.now() >= new Date(expiresAt).getTime()
}

export const hasAdminSession = () => Boolean(localStorage.getItem(STORAGE.token)) && !isSessionExpired()

api.interceptors.request.use((config) => {
  config.headers = config.headers || {}
  if (!config.headers.Accept) {
    config.headers.Accept = 'application/json'
  }

  const token = localStorage.getItem(STORAGE.token)
  const isLoginRequest = typeof config.url === 'string' && config.url.startsWith('/auth/login')
  if (token && !isLoginRequest && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`
  }

  const method = String(config.method || 'get').toUpperCase()
  const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData
  if (isMutation && !isFormData && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json'
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const config = error.config || {}
    const url = config.url || ''
    // The login + sudo endpoints legitimately return 401 on a wrong credential — don't treat those as an
    // expired session (which would log the user out mid-re-auth).
    const isAuthFlow = typeof url === 'string' && (url.startsWith('/auth/login') || url.startsWith('/auth/sudo'))

    // Sudo step-up: a 403 with sudo_required → prompt for re-confirmation, then retry the request once.
    if (status === 403 && error.response?.data?.error?.details?.sudo_required && !config._sudoRetried) {
      try {
        await requireSudo()
        config._sudoRetried = true
        return api.request(config)
      } catch (_e) {
        return Promise.reject(error)
      }
    }

    // MFA enforcement wall: a 403 with mfa_enrollment_required means the admin must set up their second factor
    // before doing anything else — send them to their profile (where the 2FA card lives).
    if (status === 403 && error.response?.data?.error?.details?.mfa_enrollment_required) {
      if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/profile')) {
        window.location.assign('/profile')
      }
      return Promise.reject(error)
    }

    // A 401 on any non-auth-flow request means the opaque session is gone/expired — drop it and bounce to login.
    if (status === 401 && !isAuthFlow) {
      clearAdminAuthStorage()
      if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/login')) {
        window.location.assign('/login')
      }
    }

    return Promise.reject(error)
  }
)

// ── what this does, and what it does not ──────────────────────────────────────────────────────────
//
// The platform error envelope is { success:false, error:{ code, message, details } }. This reads the
// envelope's GENERIC layer only — `error.message` — then falls back to axios's own `error.message`
// («Network Error», «Request failed with status code 500») and finally to `fallback`.
//
// It deliberately does NOT look inside `error.details`, where a 422 keeps the per-field Persian
// sentences. That makes it the right helper for a READ: a GET has no field to blame, and axios's
// message is what you want when the request never reached the server.
//
// It makes it the WRONG helper for a form or any other mutation, where `error.message` is the useless
// «داده‌های ارسالی معتبر نیست» and the sentence the operator needs is one layer down. Use
// `apiErrorSentence` from @/composables/useApiError there — it prefers the field sentence and
// documents the whole rule.
//
// The name is a historical one and reads more general than the behaviour is; it is kept because ~20
// call sites across the panel import it. Renaming it to something like `apiEnvelopeMessage` is a
// worthwhile follow-up, but it is a rename across pages owned by other work, not a drive-by.
export const apiErrorMessage = (error, fallback = 'در دریافت یا ثبت اطلاعات مشکلی پیش آمد. دوباره تلاش کنید.') =>
  error?.response?.data?.error?.message || error?.message || fallback

/**
 * Download a streamed CSV (or any file) from an admin endpoint through the authenticated axios instance,
 * honouring the server-supplied filename. Bearer auth rides on the request interceptor, so a plain link
 * wouldn't carry it — we fetch as a blob and trigger a client-side download.
 */
export const downloadCsv = async (path, params = {}) => {
  const response = await api.get(path, { params, responseType: 'blob' })
  const disposition = response.headers['content-disposition'] || ''
  const match = /filename="?([^"]+)"?/.exec(disposition)
  const filename = match ? decodeURIComponent(match[1]) : 'export.csv'

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export default api
